import React, { useEffect, useState } from 'react';
import { ArrowLeft, Clock, ChevronRight, Camera, MessageSquare, User, Calendar, CheckCircle, AlertCircle, MoreHorizontal, Send, Paperclip, Image as ImageIcon, Wrench } from 'lucide-react';
import { MobileLayout } from '../components/MobileLayout';
import { Tabs } from '../components/ui/Tabs';
import { Card, CardContent } from '../components/ui/Card';
// Types
type TicketStatus = 'Aperta' | 'In gestione' | 'Risolta';
type UrgencyLevel = 'Urgente' | 'Normale';
type ReportType = 'pulizia' | 'riparazione' | 'manutenzione';
interface Ticket {
  id: string;
  urgency: UrgencyLevel;
  category: string;
  room: string;
  description: string;
  fullDescription: string;
  guestName: string;
  property: string;
  timestamp: string;
  photoCount: number;
  status: TicketStatus;
  photos: string[];
  checkIn: string;
  checkOut: string;
  type: ReportType;
  interventionStatus?: string;
  messages: {
    id: string;
    sender: 'host' | 'guest';
    text: string;
    time: string;
  }[];
}
// Mock Data
const MOCK_TICKETS: Ticket[] = [{
  id: '1',
  urgency: 'Urgente',
  category: 'Problema pulizia',
  room: 'Bagno',
  description: '"Ho trovato il bagno sporco, ci sono macchie sul..."',
  fullDescription: 'Ho trovato il bagno sporco, ci sono macchie sul pavimento vicino alla doccia e lo specchio non sembra essere stato pulito correttamente. Richiedo un intervento immediato.',
  guestName: 'Marco Rossi',
  property: 'Villa Sunset',
  timestamp: 'Oggi, 14:32',
  photoCount: 2,
  status: 'Aperta',
  type: 'pulizia',
  photos: ['https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&q=80&w=400', 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&q=80&w=400'],
  checkIn: '15 Gen',
  checkOut: '20 Gen',
  messages: [{
    id: '1',
    sender: 'guest',
    text: 'Buongiorno, ho appena effettuato il check-in ma il bagno non è pulito.',
    time: '14:30'
  }, {
    id: '2',
    sender: 'host',
    text: 'Ci scusiamo per il disagio. Può mandarci delle foto?',
    time: '14:31'
  }, {
    id: '3',
    sender: 'guest',
    text: 'Ecco le foto delle macchie.',
    time: '14:32'
  }]
}, {
  id: '5',
  urgency: 'Normale',
  category: 'Manutenzione',
  room: 'Bagno ospiti',
  description: '"Lampadina fulminata nel bagno ospiti..."',
  fullDescription: 'La lampadina del bagno ospiti si è fulminata. Non è urgente ma sarebbe utile sostituirla prima del prossimo check-in.',
  guestName: 'Lucia Ferretti',
  property: 'Villa Sunset',
  timestamp: 'Ieri, 16:45',
  photoCount: 1,
  status: 'In gestione',
  type: 'manutenzione',
  interventionStatus: 'Pianificato per Gio 19',
  photos: ['https://images.unsplash.com/photo-1565814329452-e1efa11c5b89?auto=format&fit=crop&q=80&w=400'],
  checkIn: '14 Gen',
  checkOut: '18 Gen',
  messages: [{
    id: '1',
    sender: 'guest',
    text: 'La lampadina del bagno ospiti non funziona.',
    time: '16:45'
  }, {
    id: '2',
    sender: 'host',
    text: 'Grazie per la segnalazione, programmeremo la sostituzione.',
    time: '17:00'
  }]
}, {
  id: '2',
  urgency: 'Normale',
  category: 'Riparazione',
  room: 'Cucina',
  description: '"Il rubinetto della cucina perde leggermente..."',
  fullDescription: 'Il rubinetto della cucina perde leggermente acqua anche quando è chiuso completamente. È fastidioso ma non urgente.',
  guestName: 'Anna Bianchi',
  property: 'Apt Centro',
  timestamp: 'Ieri, 09:15',
  photoCount: 1,
  status: 'In gestione',
  type: 'riparazione',
  photos: ['https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&q=80&w=400'],
  checkIn: '10 Gen',
  checkOut: '17 Gen',
  messages: [{
    id: '1',
    sender: 'guest',
    text: 'Segnalo una piccola perdita in cucina.',
    time: '09:15'
  }]
}, {
  id: '3',
  urgency: 'Normale',
  category: 'Problema pulizia',
  room: 'Camera',
  description: '"C\'è polvere sotto il letto matrimoniale..."',
  fullDescription: "C'è molta polvere sotto il letto matrimoniale e ho trovato un calzino non mio.",
  guestName: 'Paolo Verdi',
  property: 'Villa Sunset',
  timestamp: '2 giorni fa',
  photoCount: 3,
  status: 'In gestione',
  type: 'pulizia',
  photos: ['https://images.unsplash.com/photo-1522771753035-4850d32f71d5?auto=format&fit=crop&q=80&w=400', 'https://images.unsplash.com/photo-1582582621959-48d27397dc69?auto=format&fit=crop&q=80&w=400', 'https://images.unsplash.com/photo-1595526114035-0d45ed16cfbf?auto=format&fit=crop&q=80&w=400'],
  checkIn: '12 Gen',
  checkOut: '19 Gen',
  messages: [{
    id: '1',
    sender: 'guest',
    text: 'La pulizia della camera lascia a desiderare.',
    time: '10:00'
  }]
}, {
  id: '4',
  urgency: 'Normale',
  category: 'Riparazione',
  room: 'Bagno',
  description: '"La doccia non scarica bene l\'acqua..."',
  fullDescription: "Lo scarico della doccia è lento, l'acqua rischia di uscire dal piatto doccia.",
  guestName: 'Elena Torre',
  property: 'Apt Centro',
  timestamp: '5 giorni fa',
  photoCount: 1,
  status: 'Risolta',
  type: 'riparazione',
  photos: ['https://images.unsplash.com/photo-1507089947368-19c1da9775ae?auto=format&fit=crop&q=80&w=400'],
  checkIn: '05 Gen',
  checkOut: '10 Gen',
  messages: [{
    id: '1',
    sender: 'guest',
    text: 'Problema con lo scarico doccia.',
    time: '18:20'
  }, {
    id: '2',
    sender: 'host',
    text: "Mandiamo subito l'idraulico.",
    time: '18:45'
  }, {
    id: '3',
    sender: 'host',
    text: 'Problema risolto, ci scusi.',
    time: '20:00'
  }]
}];
export function SegnalazioniPage() {
  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);
  const [activeTab, setActiveTab] = useState<'Tutte' | 'Aperte' | 'In gestione' | 'Risolte'>('Tutte');
  // Add workflow status state
  const [ticketStatus, setTicketStatus] = useState<'aperta' | 'presa_in_carico' | 'in_lavorazione' | 'risolta'>('aperta');
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;
  // Filter tickets
  const filteredTickets = MOCK_TICKETS.filter(ticket => {
    if (activeTab === 'Tutte') return true;
    return ticket.status === activeTab;
  });
  // Calculate pagination
  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTickets = filteredTickets.slice(startIndex, endIndex);
  // Reset to page 1 when changing tabs
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);
  // Helper for status colors
  const getStatusColor = (status: TicketStatus) => {
    switch (status) {
      case 'Aperta':
        return 'text-red-600';
      case 'In gestione':
        return 'text-amber-600';
      case 'Risolta':
        return 'text-green-600';
      default:
        return 'text-gray-600';
    }
  };
  // Helper for type badge
  const TypeBadge = ({
    type
  }: {
    type: ReportType;
  }) => {
    switch (type) {
      case 'manutenzione':
        return <span className="inline-flex items-center gap-1 bg-violet-100 text-violet-700 text-xs px-2 py-0.5 rounded-full font-medium" data-id="element-3992">
            <span data-id="element-3993">🔧</span>
            Manutenzione
          </span>;
      case 'pulizia':
        return <span className="inline-flex items-center gap-1 bg-teal-100 text-coral text-xs px-2 py-0.5 rounded-full font-medium" data-id="element-3994">
            <span data-id="element-3995">🧹</span>
            Pulizia
          </span>;
      case 'riparazione':
        return <span className="inline-flex items-center gap-1 bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-medium" data-id="element-3996">
            <span data-id="element-3997">🔨</span>
            Riparazione
          </span>;
      default:
        return null;
    }
  };
  // Helper for urgency badge
  const UrgencyBadge = ({
    level
  }: {
    level: UrgencyLevel;
  }) => {
    if (level === 'Urgente') {
      return <span className="inline-flex items-center gap-1.5 bg-red-100 text-red-700 text-xs px-2 py-0.5 rounded-full font-medium" data-id="element-3998">
          <div className="w-1.5 h-1.5 rounded-full bg-red-600 animate-pulse" data-id="element-3999" />
          Urgente
        </span>;
    }
    return <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full font-medium" data-id="element-4000">
        <div className="w-1.5 h-1.5 rounded-full bg-amber-600" data-id="element-4001" />
        Normale
      </span>;
  };
  // --- DETAIL VIEW ---
  if (selectedTicket) {
    // Handler for advancing workflow
    const handleAdvanceStatus = () => {
      if (ticketStatus === 'aperta') setTicketStatus('presa_in_carico');else if (ticketStatus === 'presa_in_carico') setTicketStatus('in_lavorazione');else if (ticketStatus === 'in_lavorazione') setTicketStatus('risolta');
    };
    const handleSkipToResolved = () => {
      setTicketStatus('risolta');
    };
    return <MobileLayout data-id="element-4002">
        {/* Header */}
        <div className="mb-6" data-id="element-4003">
          <button onClick={() => setSelectedTicket(null)} className="flex items-center gap-1 text-sm text-gray-500 hover:text-navy mb-3 transition-colors" data-id="element-4004">
            <ArrowLeft size={16} data-id="element-4005" />
            Indietro
          </button>

          <div className="flex flex-col gap-3" data-id="element-4006">
            <div data-id="element-4007">
              <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-4008">
                {selectedTicket.category} - {selectedTicket.room}
              </h1>
              <p className="text-sm text-gray-500 mt-1" data-id="element-4009">
                {selectedTicket.property} • {selectedTicket.timestamp}
              </p>
            </div>
            <div className="flex items-center gap-2" data-id="element-4010">
              <UrgencyBadge level={selectedTicket.urgency} data-id="element-4011" />
              <TypeBadge type={selectedTicket.type} data-id="element-4012" />
            </div>
          </div>
        </div>

        {/* Maintenance intervention banner */}
        {selectedTicket.type === 'manutenzione' && selectedTicket.interventionStatus && <div className="bg-violet-50 border border-violet-200 rounded-xl p-4 mb-6" data-id="element-4013">
              <div className="flex items-center justify-between" data-id="element-4014">
                <div className="flex items-center gap-3" data-id="element-4015">
                  <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center" data-id="element-4016">
                    <Wrench size={20} className="text-violet-600" data-id="element-4017" />
                  </div>
                  <div data-id="element-4018">
                    <p className="text-sm font-medium text-violet-900" data-id="element-4019">
                      Intervento straordinario collegato
                    </p>
                    <p className="text-sm text-violet-700" data-id="element-4020">
                      {selectedTicket.interventionStatus}
                    </p>
                  </div>
                </div>
                <button className="px-4 py-2 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-700 transition-colors" data-id="element-4021">
                  Vedi intervento →
                </button>
              </div>
            </div>}

        <div className="flex flex-col lg:flex-row gap-6" data-id="element-4022">
          {/* Column 1: Details */}
          <div className="flex-1 space-y-6" data-id="element-4023">
            <div className="bg-white rounded-xl border border-gray-200 p-6" data-id="element-4024">
              {/* Guest Section */}
              <div className="mb-6 pb-6 border-b border-gray-100" data-id="element-4025">
                <h3 className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-3" data-id="element-4026">
                  Guest
                </h3>
                <div className="flex items-center gap-3 mb-2" data-id="element-4027">
                  <div className="w-10 h-10 rounded-full bg-teal-50 flex items-center justify-center text-teal-deep" data-id="element-4028">
                    <User size={20} data-id="element-4029" />
                  </div>
                  <div data-id="element-4030">
                    <p className="font-medium text-navy" data-id="element-4031">
                      {selectedTicket.guestName}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-4032">
                      <Calendar size={12} data-id="element-4033" />
                      <span data-id="element-4034">
                        Check-in: {selectedTicket.checkIn} - Check-out:{' '}
                        {selectedTicket.checkOut}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Details Section */}
              <div className="mb-6 pb-6 border-b border-gray-100" data-id="element-4035">
                <h3 className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-3" data-id="element-4036">
                  Dettagli
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-4037">
                  <div data-id="element-4038">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-4039">
                      Stanza
                    </span>
                    <span className="text-sm font-medium text-navy" data-id="element-4040">
                      {selectedTicket.room}
                    </span>
                  </div>
                  <div data-id="element-4041">
                    <span className="text-xs text-gray-500 block mb-1" data-id="element-4042">
                      Categoria
                    </span>
                    <span className="text-sm font-medium text-navy" data-id="element-4043">
                      {selectedTicket.category}
                    </span>
                  </div>
                </div>
              </div>

              {/* Description Section */}
              <div className="mb-6 pb-6 border-b border-gray-100" data-id="element-4044">
                <h3 className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-3" data-id="element-4045">
                  Descrizione
                </h3>
                <p className="text-sm text-gray-700 leading-relaxed" data-id="element-4046">
                  {selectedTicket.fullDescription}
                </p>
              </div>

              {/* Photos Section */}
              <div data-id="element-4047">
                <h3 className="text-xs text-gray-400 uppercase tracking-wider font-medium mb-3" data-id="element-4048">
                  Foto ({selectedTicket.photos.length})
                </h3>
                <div className="grid grid-cols-3 gap-2" data-id="element-4049">
                  {selectedTicket.photos.map((photo, idx) => <div key={idx} className="aspect-square rounded-lg overflow-hidden border border-gray-200 bg-gray-100" data-id="element-4050">
                      <img src={photo} alt={`Evidence ${idx + 1}`} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" data-id="element-4051" />
                    </div>)}
                </div>
              </div>
            </div>
          </div>

          {/* Column 2: NEW Gestione Panel - WIDER SIDEBAR */}
          <div className="w-full lg:w-[420px] flex-shrink-0" data-id="element-4052">
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden sticky top-6" data-id="element-4053">
              {/* SECTION 1: AVANZAMENTO */}
              <div className="p-4" data-id="element-4054">
                <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-4 font-medium" data-id="element-4055">
                  Avanzamento
                </h3>

                {/* Progress Bar */}
                <div className="relative" data-id="element-4056">
                  <div className="flex items-center justify-between mb-2" data-id="element-4057">
                    {/* Step 1: Aperta */}
                    <div className="flex flex-col items-center flex-1" data-id="element-4058">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold transition-all ${ticketStatus === 'aperta' ? 'bg-teal-deep text-white shadow-lg scale-110' : ticketStatus !== 'aperta' ? 'bg-teal-deep text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-4059">
                        1
                      </div>
                      <span className={`text-[11px] mt-2 text-center whitespace-nowrap ${ticketStatus === 'aperta' ? 'text-teal-deep font-medium' : ticketStatus !== 'aperta' ? 'text-teal-deep' : 'text-gray-400'}`} data-id="element-4060">
                        Aperta
                      </span>
                    </div>

                    {/* Line 1 */}
                    <div className={`h-0.5 flex-1 mx-0.5 ${ticketStatus !== 'aperta' ? 'bg-teal-deep h-[2px]' : 'bg-gray-200 h-[2px]'}`} data-id="element-4061" />

                    {/* Step 2: Presa in carico */}
                    <div className="flex flex-col items-center flex-1" data-id="element-4062">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold transition-all ${ticketStatus === 'presa_in_carico' ? 'bg-teal-deep text-white shadow-lg scale-110' : ticketStatus === 'in_lavorazione' || ticketStatus === 'risolta' ? 'bg-teal-deep text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-4063">
                        2
                      </div>
                      <span className={`text-[11px] mt-2 text-center whitespace-nowrap ${ticketStatus === 'presa_in_carico' ? 'text-teal-deep font-medium' : ticketStatus === 'in_lavorazione' || ticketStatus === 'risolta' ? 'text-teal-deep' : 'text-gray-400'}`} data-id="element-4064">
                        Presa in carico
                      </span>
                    </div>

                    {/* Line 2 */}
                    <div className={`h-0.5 flex-1 mx-0.5 ${ticketStatus === 'in_lavorazione' || ticketStatus === 'risolta' ? 'bg-teal-deep h-[2px]' : 'bg-gray-200 h-[2px]'}`} data-id="element-4065" />

                    {/* Step 3: In lavorazione */}
                    <div className="flex flex-col items-center flex-1" data-id="element-4066">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold transition-all ${ticketStatus === 'in_lavorazione' ? 'bg-teal-deep text-white shadow-lg scale-110' : ticketStatus === 'risolta' ? 'bg-teal-deep text-white' : 'bg-gray-200 text-gray-500'}`} data-id="element-4067">
                        3
                      </div>
                      <span className={`text-[11px] mt-2 text-center whitespace-nowrap ${ticketStatus === 'in_lavorazione' ? 'text-teal-deep font-medium' : ticketStatus === 'risolta' ? 'text-teal-deep' : 'text-gray-400'}`} data-id="element-4068">
                        In lavorazione
                      </span>
                    </div>

                    {/* Line 3 */}
                    <div className={`h-0.5 flex-1 mx-0.5 ${ticketStatus === 'risolta' ? 'bg-teal-deep h-[2px]' : 'bg-gray-200 h-[2px]'}`} data-id="element-4069" />

                    {/* Step 4: Risolta */}
                    <div className="flex flex-col items-center flex-1" data-id="element-4070">
                      <div className={`w-8 h-8 rounded-md flex items-center justify-center text-xs font-bold transition-all ${ticketStatus === 'risolta' ? 'bg-teal-deep text-white shadow-lg scale-110' : 'bg-gray-200 text-gray-500'}`} data-id="element-4071">
                        4
                      </div>
                      <span className={`text-[11px] mt-2 text-center whitespace-nowrap ${ticketStatus === 'risolta' ? 'text-teal-deep font-medium' : 'text-gray-400'}`} data-id="element-4072">
                        Risolta
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* SECTION 2: PROSSIMA AZIONE */}
              <div className="p-4 border-t border-gray-100" data-id="element-4073">
                <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3 font-medium" data-id="element-4074">
                  Prossima Azione
                </h3>

                {ticketStatus !== 'risolta' ? <>
                    <button onClick={handleAdvanceStatus} className="w-full bg-teal-deep text-white rounded-full py-3 font-medium text-sm hover:bg-[#50BFD9] transition-colors mb-2" data-id="element-4075">
                      {ticketStatus === 'aperta' && '📋 Prendi in carico'}
                      {ticketStatus === 'presa_in_carico' && '🔧 Avvia lavorazione'}
                      {ticketStatus === 'in_lavorazione' && '✅ Segna risolta'}
                    </button>

                    <p className="text-xs text-gray-500 mb-3" data-id="element-4076">
                      {ticketStatus === 'aperta' && 'Il guest verrà notificato che la segnalazione è stata presa in carico'}
                      {ticketStatus === 'presa_in_carico' && 'Il fornitore verrà notificato per iniziare i lavori'}
                      {ticketStatus === 'in_lavorazione' && 'Il guest verrà notificato che il problema è stato risolto'}
                    </p>

                    <button onClick={handleSkipToResolved} className="text-sm text-gray-500 hover:text-navy transition-colors" data-id="element-4077">
                      Salta a risolta →
                    </button>
                  </> : <p className="text-sm text-green-600 font-medium" data-id="element-4078">
                    ✓ Completata
                  </p>}
              </div>

              {/* SECTION 3: FORNITORE */}
              <div className="p-4 border-t border-gray-100" data-id="element-4079">
                <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3 font-medium" data-id="element-4080">
                  Fornitore
                </h3>

                <p className="font-medium text-navy mb-1" data-id="element-4081">CleanPro Services</p>
                <p className="text-xs text-green-600 mb-2" data-id="element-4082">
                  ✅ Notificato il 18 Gen alle 14:45
                </p>
                <button className="text-xs text-teal-deep hover:underline" data-id="element-4083">
                  Cambia
                </button>
              </div>

              {/* SECTION 4: COSTO INTERVENTO */}
              <div className="p-4 border-t border-gray-100" data-id="element-4084">
                <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-3 font-medium" data-id="element-4085">
                  Costo Intervento
                </h3>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-id="element-4086">
                  <p className="text-sm text-amber-900 mb-1" data-id="element-4087">
                    ⚡ Verrà scalato 1 intervento dal pacchetto
                  </p>
                  <p className="text-xs text-amber-700" data-id="element-4088">
                    Pacchetto attuale: 7/10 usati
                  </p>
                </div>
              </div>

              {/* SECTION 5: NOTA INTERNA */}
              <div className="p-4 border-t border-gray-100" data-id="element-4089">
                <h3 className="text-xs text-gray-400 uppercase tracking-wider mb-1 font-medium" data-id="element-4090">
                  Nota Interna{' '}
                  <span className="text-gray-400 normal-case" data-id="element-4091">(opzionale)</span>
                </h3>

                <textarea rows={3} className="w-full mt-2 border border-gray-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none resize-none" placeholder="Solo per te, il guest non vedrà questa nota" data-id="element-4092" />
              </div>
            </div>
          </div>
        </div>
      </MobileLayout>;
  }
  // --- LIST VIEW ---
  return <MobileLayout data-id="element-4093">
      {/* Page Header */}
      <div className="mb-6 lg:mb-8" data-id="element-4094">
        <h1 className="text-xl lg:text-2xl font-bold text-navy" data-id="element-4095">
          Segnalazioni
        </h1>
      </div>
      {/* Tabs - Pill style (same as Pulizie) */}
      <Tabs className="mb-6" scrollable value={activeTab} onValueChange={id => setActiveTab(id as 'Tutte' | 'Aperte' | 'In gestione' | 'Risolte')} items={(['Tutte', 'Aperte', 'In gestione', 'Risolte'] as const).map(tab => {
      const count = tab === 'Tutte' ? MOCK_TICKETS.length : MOCK_TICKETS.filter(t => t.status === tab).length;
      const isActive = activeTab === tab;
      return {
        id: tab,
        label: tab,
        badge: count > 0 ? <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full ${isActive ? 'bg-white text-coral' : 'bg-coral text-white'}`} data-id="element-4097">
                    {count}
                  </span> : undefined
      };
    })} data-id="element-4096" />
      {/* Tickets List */}
      <div className="space-y-3" data-id="element-4098">
        {paginatedTickets.length > 0 ? <>
            {paginatedTickets.map(ticket => <Card key={ticket.id} onClick={() => setSelectedTicket(ticket)} className="hover:border-gray-300 cursor-pointer transition-all group" data-id="element-4099">
                <CardContent data-id="element-4100">
                  {/* Row 1: Urgency + Timestamp */}
                  <div className="flex justify-between items-start mb-2" data-id="element-4101">
                    <UrgencyBadge level={ticket.urgency} data-id="element-4102" />
                    <span className="text-xs text-gray-400 flex items-center gap-1" data-id="element-4103">
                      <Clock size={12} data-id="element-4104" />
                      {ticket.timestamp}
                    </span>
                  </div>

                  {/* Row 2: Category + Room */}
                  <h3 className="text-sm font-semibold text-navy mb-1" data-id="element-4105">
                    {ticket.category} - {ticket.room}
                  </h3>

                  {/* Row 3: Description Preview */}
                  <p className="text-xs text-gray-500 mb-3 line-clamp-1" data-id="element-4106">
                    {ticket.description}
                  </p>

                  {/* Maintenance intervention link */}
                  {ticket.type === 'manutenzione' && ticket.interventionStatus && <div className="mt-3 pt-3 border-t border-gray-100" data-id="element-4107">
                        <div className="flex items-center justify-between" data-id="element-4108">
                          <div className="flex items-center gap-2" data-id="element-4109">
                            <span className="text-violet-500" data-id="element-4110">🔧</span>
                            <span className="text-xs text-gray-600" data-id="element-4111">
                              Intervento collegato:
                            </span>
                            <span className="text-xs text-violet-600 font-medium" data-id="element-4112">
                              {ticket.interventionStatus}
                            </span>
                          </div>
                          <button onClick={e => {
                  e.stopPropagation();
                }} className="text-xs text-violet-600 hover:text-violet-700 font-medium" data-id="element-4113">
                            Vedi →
                          </button>
                        </div>
                      </div>}

                  {/* Row 4: Footer Info */}
                  <div className="flex items-center justify-between mt-3" data-id="element-4114">
                    <div className="flex items-center gap-3 text-xs text-gray-400" data-id="element-4115">
                      <span className="flex items-center gap-1" data-id="element-4116">
                        <User size={12} data-id="element-4117" />
                        {ticket.guestName}
                      </span>
                      <span className="hidden sm:inline" data-id="element-4118">•</span>
                      <span className="hidden sm:inline" data-id="element-4119">
                        {ticket.property}
                      </span>
                    </div>

                    <div className="flex items-center gap-3" data-id="element-4120">
                      {ticket.photoCount > 0 && <span className="inline-flex items-center gap-1 text-xs text-gray-500" data-id="element-4121">
                          <ImageIcon size={12} data-id="element-4122" />
                          {ticket.photoCount} foto
                        </span>}
                      <button onClick={e => {
                  e.stopPropagation();
                  setSelectedTicket(ticket);
                }} className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors" data-id="element-4123">
                        Vai al dettaglio
                      </button>
                    </div>
                  </div>
                </CardContent>
              </Card>)}
            {totalPages > 1 && <div className="flex items-center justify-between pt-4" data-id="element-4124">
                <div className="text-sm text-gray-500" data-id="element-4125">
                  Mostrando {startIndex + 1}-
                  {Math.min(endIndex, filteredTickets.length)} di{' '}
                  {filteredTickets.length}
                </div>

                <div className="flex items-center gap-2" data-id="element-4126">
                  <button onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={currentPage === 1} className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]" data-id="element-4127">
                    Precedente
                  </button>

                  <div className="flex items-center gap-1" data-id="element-4128">
                    {Array.from({
                length: totalPages
              }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage(page)} className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${currentPage === page ? 'bg-teal-deep text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-4129">
                        {page}
                      </button>)}
                  </div>

                  <button onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={currentPage === totalPages} className="px-3 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors min-h-[44px]" data-id="element-4130">
                    Successivo
                  </button>
                </div>
              </div>}
          </> : <Card data-id="element-4131">
            <CardContent className="p-12 text-center text-gray-500" data-id="element-4132">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4133">
                <CheckCircle size={32} className="text-gray-300" data-id="element-4134" />
              </div>
              <p className="font-medium" data-id="element-4135">Nessuna segnalazione trovata</p>
              <p className="text-sm mt-1" data-id="element-4136">
                Non ci sono ticket in questa categoria.
              </p>
            </CardContent>
          </Card>}
      </div>{' '}
    </MobileLayout>;
}